import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Grid,
  Autocomplete,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';

const JournalVoucher = () => {
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState([
    { ledgerId: null, amount: '', type: 'credit', description: '' },
    { ledgerId: null, amount: '', type: 'debit', description: '' }
  ]);

  // Fetch all ledgers
  useEffect(() => {
    fetchLedgers();
  }, []);

  const fetchLedgers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/ledgers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch ledgers');
      }

      const data = await response.json();
      const ledgersData = data.data || data || [];
      setLedgers(ledgersData);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error loading ledgers',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index][field] = value;
    setEntries(newEntries);
  };

  const handleAddRow = () => {
    setEntries([...entries, { ledgerId: null, amount: '', type: 'debit', description: '' }]);
  };

  const handleRemoveRow = (index) => {
    if (entries.length <= 2) {
      setSnackbar({
        open: true,
        message: 'Journal voucher must have at least 2 entries',
        severity: 'warning'
      });
      return;
    }
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
  };

  const calculateTotals = () => {
    let totalDebit = 0;
    let totalCredit = 0;

    entries.forEach(entry => {
      const amount = parseFloat(entry.amount) || 0;
      if (entry.type === 'debit') {
        totalDebit += amount;
      } else {
        totalCredit += amount;
      }
    });

    return { totalDebit, totalCredit };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!date) {
      setSnackbar({ open: true, message: 'Please select a date', severity: 'error' });
      return;
    }

    for (const entry of entries) {
      if (!entry.ledgerId || !entry.amount) {
        setSnackbar({ open: true, message: 'All rows must have a ledger and amount', severity: 'error' });
        return;
      }
    }

    const { totalDebit, totalCredit } = calculateTotals();
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      setSnackbar({
        open: true,
        message: `Total Debit (${totalDebit}) must equal Total Credit (${totalCredit})`,
        severity: 'error'
      });
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        date,
        entries: entries.map(e => ({
          ledgerId: e.ledgerId._id,
          amount: parseFloat(e.amount),
          type: e.type,
          description: e.description
        }))
      };

      const response = await fetch(`${baseURL}/accounting-entries/manual/journal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create journal entry');
      }

      setSnackbar({
        open: true,
        message: 'Journal entry created successfully',
        severity: 'success'
      });

      // Reset form
      setDate(new Date().toISOString().split('T')[0]);
      setEntries([
        { ledgerId: null, amount: '', type: 'credit', description: '' },
        { ledgerId: null, amount: '', type: 'debit', description: '' }
      ]);

    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Error creating journal entry',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const { totalDebit, totalCredit } = calculateTotals();

  return (
    <>
      <GirviPageShell
        title="Journal Voucher"
        subtitle="Accounting · Vouchers"
        chipLabel="Accounting"
        maxWidth="xl"
       compact>
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 0.5 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TableContainer sx={{ mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="30%">Ledger</TableCell>
                    <TableCell width="15%">Type</TableCell>
                    <TableCell width="15%">Amount</TableCell>
                    <TableCell width="30%">Description</TableCell>
                    <TableCell width="10%">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          options={ledgers}
                          getOptionLabel={(option) => 
                            `${option.firstName} ${option.lastName}${option.groupCode ? ` (${option.groupCode})` : ''}`
                          }
                          value={entry.ledgerId}
                          onChange={(e, newValue) => handleEntryChange(index, 'ledgerId', newValue)}
                          renderInput={(params) => <TextField {...params} label="Select Ledger" size="small" />}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={entry.type}
                            onChange={(e) => handleEntryChange(index, 'type', e.target.value)}
                          >
                            <MenuItem value="credit">Credit</MenuItem>
                            <MenuItem value="debit">Debit</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          type="number"
                          size="small"
                          value={entry.amount}
                          onChange={(e) => handleEntryChange(index, 'amount', e.target.value)}
                          placeholder="0.00"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={entry.description}
                          onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                          placeholder="Description"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleRemoveRow(index)} color="error" disabled={entries.length <= 2}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button startIcon={<AddIcon />} onClick={handleAddRow} variant="outlined">
                Add Row
              </Button>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle1" color={totalDebit !== totalCredit ? 'error' : 'success.main'}>
                  Total Debit: {totalDebit.toFixed(2)} | Total Credit: {totalCredit.toFixed(2)}
                </Typography>
                {Math.abs(totalDebit - totalCredit) > 0.01 && (
                  <Typography variant="caption" color="error">
                    Difference: {Math.abs(totalDebit - totalCredit).toFixed(2)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                type="submit" 
                variant="contained" 
                size="large"
                disabled={submitting || Math.abs(totalDebit - totalCredit) > 0.01}
              >
                {submitting ? 'Saving...' : 'Save Journal Entry'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </GirviPageShell>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default JournalVoucher;